import { AxiosRequestConfig } from "axios";
import { useCallback } from "react";

import { Documentation } from "../models";
import { RepDocument } from "../models/documentation";
import * as Queries from "./queries";
import { useQueryWrapper, useRepPortalApi } from "./repPortalApi";

export function useDocumentationApi() {
	const { Get, Post } = useRepPortalApi();

	const loadRepDocumentation = useCallback(
		function (repId: number, config?: AxiosRequestConfig) {
			return async function () {
				return await Get<Documentation>(
					"GetRepresentativeDocumentationInfo",
					{
						repId,
					},
					config,
				);
			};
		},
		[Get],
	);

	const saveRepDocumentation = useCallback(
		async function (documentation: Documentation, repId: number, config?: AxiosRequestConfig) {
			await Promise.all(Object.values(documentation).map(SaveDocument));

			function SaveDocument(doc: RepDocument) {
				const { definitionId, completed, files, filesUploaded } = doc;
				const allFiles = filesUploaded.concat(files);

				const formData = new FormData();
				formData.append("repId", repId.toString());
				formData.append("definitionId", definitionId.toString());
				formData.append("completed", completed.toString());

				const fileProps = allFiles.map((f) => ({
					...f,
					url: f.file ? null : f.url,
					file: undefined,
				}));

				formData.append("fileData", JSON.stringify(fileProps));

				allFiles.forEach((f, index) => {
					if (f.file) {
						formData.append(`file_${index}`, f.file);
					}
				});

				return Post<boolean, any>("SaveDocumentation", {}, formData, config);
			}
		},
		[Post],
	);

	const useDocumentationQuery = (repId: number) =>
		useQueryWrapper(Queries.DOCUMENTATION, [repId, loadRepDocumentation], (context) =>
			loadRepDocumentation(repId, { signal: context.signal })(),
		);

	return { loadRepDocumentation, saveRepDocumentation, useDocumentationQuery };
}
